import { INPUT_TYPE } from "../../engine/indicators/indicatorTypes";
import { DRAWING_LEVEL_FORMAT } from "../../engine/drawings/drawingLevels";
import { DRAWING_TOOL } from "../../engine/drawings/drawingTools";

/**
 * Rows of the settings modal of a drawing: which fields of its draft a row
 * shows. The names are as in PO, like the inputs of indicators (E26).
 */
const MAIN_LINE = { key: "main", label: "Main Line", visibleKey: "isVisible", colorKey: "color", widthKey: "width" };
const MEDIAN = { key: "median", label: "Median", visibleKey: "isMedianVisible", colorKey: "color", widthKey: "width" };
const LEVELS = { key: "levels", label: "Levels", visibleKey: "isLevelsVisible", colorKey: "levelsColor" };
const VALUES = { key: "values", label: "Values", visibleKey: "isValuesVisible", colorKey: "valuesColor" };
const BACKGROUND = { key: "background", label: "Background", visibleKey: "isBackgroundVisible", colorKey: "backgroundColor" };
const BACKGROUND_FLAG_ONLY = { key: "background", label: "Background", visibleKey: "isBackgroundVisible" };

const LEVEL_FORMAT_INPUT = {
  key: "levelFormat",
  label: "Levels",
  type: INPUT_TYPE.SELECT,
  options: Object.values(DRAWING_LEVEL_FORMAT),
};

const DEFAULT_SETTINGS = { rows: [MAIN_LINE] };

const TOOL_SETTINGS = {
  [DRAWING_TOOL.FIBONACCI_RETRACEMENT]: { inputs: [LEVEL_FORMAT_INPUT], rows: [MAIN_LINE, LEVELS, VALUES] },
  [DRAWING_TOOL.FIBONACCI_FAN]: { rows: [MAIN_LINE, LEVELS] },
  [DRAWING_TOOL.PARALLEL_CHANNEL]: { rows: [MAIN_LINE, BACKGROUND] },
  [DRAWING_TOOL.RECTANGLE]: { rows: [MAIN_LINE, BACKGROUND] },
  [DRAWING_TOOL.ANDREWS_PITCHFORK]: { rows: [MEDIAN], rowsAfterLevels: [BACKGROUND_FLAG_ONLY] },
};

/**
 * @param {string} toolId - DRAWING_TOOL value
 * @returns {{ inputs?: object[], rows: object[], rowsAfterLevels?: object[] }} inputs: with them the modal has the
 *   Inputs / Styles tabs; the levels of a drawing go between the two lists of rows
 */
export const drawingSettingsOf = (toolId) => TOOL_SETTINGS[toolId] || DEFAULT_SETTINGS;

/**
 * @param {string} toolId - DRAWING_TOOL value
 * @returns {boolean} whether the modal has the row that hides the whole drawing
 */
export const hasMainLineRow = (toolId) => drawingSettingsOf(toolId).rows.includes(MAIN_LINE);

/**
 * @param {object[]} rows
 * @param {object} draft - see useDrawingDraft
 * @returns {object} `plotStyles` of StylesTab
 */
export const rowsToPlotStyles = (rows, draft) =>
  Object.fromEntries(
    rows.map(({ key, visibleKey, colorKey, widthKey }) => [
      key,
      { isVisible: draft[visibleKey], color: draft[colorKey], width: draft[widthKey] },
    ]),
  );

/**
 * @param {object} row
 * @param {{ isVisible?: boolean, color?: string, width?: number }} change - from StylesTab
 * @returns {object} the same change of the draft
 */
export const rowChangeToDraft = ({ visibleKey, colorKey, widthKey }, { isVisible, color, width }) => ({
  ...(isVisible === undefined ? {} : { [visibleKey]: isVisible }),
  ...(color === undefined ? {} : { [colorKey]: color }),
  ...(width === undefined ? {} : { [widthKey]: width }),
});
